import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Mail, User, Lock, ArrowRight } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const SalePageCheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accountInfo, setAccountInfo] = useState<any>(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    fullName: ''
  });

  const paymentId = searchParams.get('payment_id');

  useEffect(() => {
    // Auto-fill form if we have checkout data
    const storedData = localStorage.getItem('salePageCheckout');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        // We'll need to collect this info in checkout form
      } catch (err) {
        console.error('Error parsing stored data:', err);
      }
    }
    setLoading(false);
  }, []);

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.phone || !formData.fullName) {
      setError('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    if (!paymentId) {
      setError('ไม่พบข้อมูลการชำระเงิน');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const storedData = localStorage.getItem('salePageCheckout');
      const checkoutData = storedData ? JSON.parse(storedData) : {};

      const functions = getFunctions(undefined, 'us-central1');
      const createAccount = httpsCallable(functions, 'createSalePageAccount');

      const result = await createAccount({
        email: formData.email,
        phone: formData.phone,
        fullName: formData.fullName,
        plan: checkoutData.plan,
        planName: checkoutData.planName || (checkoutData.plan === 'premium' ? 'Premium' : 'Basic'),
        billing: checkoutData.billing,
        amount: checkoutData.amount,
        paymentId: paymentId
      });

      const data = result.data as any;
      
      if (data.success) {
        setAccountInfo({
          username: data.username,
          password: formData.phone.replace(/\D/g, '')
        });
        
        // Clear checkout data
        localStorage.removeItem('salePageCheckout');
      } else {
        setError('เกิดข้อผิดพลาดในการสร้างบัญชี');
      }
    } catch (err: any) {
      console.error('Error creating account:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการสร้างบัญชี');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !accountInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-950 to-black flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-emerald-400" />
          <p>กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (accountInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-950 to-black text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-2xl mx-auto">
            {/* Success Icon */}
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <CheckCircle className="w-16 h-16 text-green-400" />
              </div>
              <h1 className="text-4xl font-bold mb-4">ชำระเงินสำเร็จ!</h1>
              <p className="text-gray-400 text-lg">บัญชีของคุณถูกสร้างเรียบร้อยแล้ว</p>
            </div>

            {/* Account Info Card */}
            <div className="bg-gradient-to-br from-emerald-900/50 to-green-900/50 backdrop-blur-xl border border-emerald-500/50 rounded-2xl p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-6 h-6 text-emerald-400" />
                <h2 className="text-2xl font-bold">ข้อมูลการเข้าสู่ระบบ</h2>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-gray-900/50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-400">Username (ชื่อผู้ใช้)</span>
                  </div>
                  <p className="text-2xl font-bold font-mono text-emerald-300">{accountInfo.username}</p>
                </div>

                <div className="bg-gray-900/50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Lock className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-400">Password (รหัสผ่าน)</span>
                  </div>
                  <p className="text-2xl font-bold font-mono text-emerald-300">{accountInfo.password}</p>
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-yellow-200">
                  <strong>⚠️ สำคัญ:</strong> กรุณาบันทึกข้อมูลนี้ไว้ คุณจะได้รับอีเมลยืนยันพร้อมข้อมูลการเข้าสู่ระบบ
                </p>
              </div>

              <button
                onClick={() => window.location.href = 'https://saledi.web.app/login'}
                className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-lg shadow-emerald-500/50"
              >
                เข้าสู่ระบบเลย
                <ArrowRight size={24} />
              </button>
            </div>

            {/* Email Sent Notice */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center">
              <Mail className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
              <p className="text-emerald-200">
                เราได้ส่งอีเมลยืนยันพร้อมข้อมูลการเข้าสู่ระบบไปที่ <strong>{formData.email}</strong> แล้ว
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-950 to-black text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
            <h1 className="text-4xl font-bold mb-4">ชำระเงินสำเร็จ!</h1>
            <p className="text-gray-400 text-lg">กรุณากรอกข้อมูลเพื่อสร้างบัญชีของคุณ</p>
          </div>

          {/* Form Card */}
          <div className="bg-gradient-to-br from-emerald-900/50 to-green-900/50 backdrop-blur-xl border border-emerald-500/50 rounded-2xl p-8">
            <form onSubmit={handleCreateAccount} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">ชื่อ-นามสกุล</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-emerald-500/30 rounded-xl focus:outline-none focus:border-emerald-400 transition-colors text-white"
                  placeholder="กรอกชื่อ-นามสกุล"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">อีเมล</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-emerald-500/30 rounded-xl focus:outline-none focus:border-emerald-400 transition-colors text-white"
                  placeholder="example@email.com"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Username ของคุณจะเป็นส่วนหน้าของอีเมล</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">เบอร์โทรศัพท์</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-emerald-500/30 rounded-xl focus:outline-none focus:border-emerald-400 transition-colors text-white"
                  placeholder="0812345678"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">รหัสผ่านของคุณจะเป็นเบอร์โทรศัพท์</p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-lg shadow-emerald-500/50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    กำลังสร้างบัญชี...
                  </>
                ) : (
                  <>
                    สร้างบัญชีและรับข้อมูลการเข้าสู่ระบบ
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalePageCheckoutSuccess;
